import type { Lang } from "./copy";
// 🔴 ADR-037 — the denominator a teacher reads is the level count, not a digit
// typed twice. Both language variants below carried their own `5`, and both
// were wrong the day the game shipped three levels. Deep import: this copy
// table has no use for the content tables the curriculum barrel re-exports.
import { LEVEL_COUNT } from "../../../shared/curriculum/progress";

/**
 * D5's copy — the student page and the four teacher controls.
 *
 * ⚠️ A SEPARATE FILE FROM `copy.ts`, the `adminCopy.ts` precedent one tier down:
 * that file is D1–D7's shared vocabulary and this is one screen's, which is
 * large enough to bury it. `Lang` is imported rather than redeclared.
 *
 * 🔴 THE HARDEST SENTENCE IN THIS FILE IS `noOtherData`, AND IT IS THE ONE THAT
 * MUST NOT BE CUT. A `Player` row holds a name, an avatar, an age band and a
 * roll number — nothing else. No date of birth, no address, no photograph, no
 * gender, no notes; `check-no-free-text.mjs` fails the build if anyone adds a
 * notes column, and ADR-004 is why. A teacher who does not know that will read
 * a page with no date of birth as a page that failed to load half its content,
 * and will go looking for a field that does not exist and should not.
 */

export const STUDENT_COPY = {
  en: {
    /* ── identity ── */
    back: "Back to class",
    roll: "Roll number",
    band: "Age band",
    className: "Class",
    noRoll: "No roll number",
    /** 🔴 The sentence that stops a hunt for data the product refuses to hold. */
    noOtherData:
      "Kalqy stores a child’s name, roll number and age band, and nothing else — no date of birth, address, photograph or notes. That is deliberate: the less it holds, the less there is to lose.",

    /* ── parent ── */
    parentTitle: "Parent",
    parentName: "Name",
    parentPhone: "Phone",
    /** ⚠️ Masked server-side. The full number never reaches this screen. */
    phoneMasked: "Shown partly hidden. The full number stays on the server.",
    noParent: "No parent linked to this child.",
    noParentWhy:
      "Progress reports cannot be sent until a parent is linked. The roster import is what links them.",

    /* ── progress ── */
    progressTitle: "Progress",
    coins: "Coins",
    stickers: "Stickers",
    streak: "Day streak",
    /**
     * ⚠️ ONLY DRAWN WHEN THE STREAK IS RUNNING. "Keep it going!" under a zero
     * is encouragement aimed at nothing — the child has not started, which is a
     * different sentence and one the empty state below already says.
     */
    streakCaption: "Days in a row",
    lastPlayed: "Last played",
    neverPlayed: "Has not played yet",
    notStartedHint:
      "Nothing has been recorded for this child yet. Hand them the laptop to begin.",

    /* ── skills ── */
    skillsTitle: "Skills",
    /** 🔴 Content skills read as a LEVEL. ENGINEERING-GUIDE.md §8, and never a percentage. */
    contentSkills: "Learning",
    motorSkills: "Movement",
    level: (n: number) => `Level ${n} of ${LEVEL_COUNT}`,
    notStartedSkill: "Not started",
    activities: (n: number) => `${n} activities`,
    strongest: "Strongest",
    /** ⚠️ Movement is counted, not levelled — it is earned across all games. */
    motorHint:
      "Movement skills are earned across every game, so they are counted rather than levelled.",

    /* ── insight ── */
    insightTitle: "Insight",
    strengths: "Strengths",
    focus: "Worth practising",

    /* ── attendance · T7 ── */
    attendanceTitle: "Attendance",
    markAbsent: "Mark absent today",
    unmarkAbsent: "Undo — they were here",
    absentToday: "Marked absent today",
    /**
     * 🔴 SAYS WHAT IT ACTUALLY CHANGES. `attention.ts` discounts absent days
     * from the idle count and does not touch accuracy — so this stops a child
     * being chased for not playing while they were away, and says nothing about
     * how they answer when they are there.
     */
    absentWhy:
      "A day marked absent is not counted as a day they did not play, so being away does not look like falling behind. It does not change their accuracy.",

    /* ── band override · T4 ── */
    bandTitle: "Age band",
    bandWhy:
      "The band decides which games this child is offered next. It is a starting point, not an assessment — changing it records nothing about them.",
    bandSave: "Change band",
    bandSaved: "Band changed.",

    /* ── move class ── */
    moveTitle: "Move to another class",
    moveWhy:
      "Their history, roll number and parent move with them. Use this when a child was enrolled in the wrong section.",
    moveTo: "Move into",
    moveRoll: "Roll number in the new class",
    moveRollWhy:
      "Roll numbers are unique inside a class. Leave this alone to keep theirs; set it if the new class already uses that number.",
    move: "Move child",
    moved: "Child moved.",

    /* ── sessions · T1 ── */
    sessionsTitle: "Sittings",
    sessionsWhy:
      "Each time this child played. Use this if a session was started on the wrong child.",
    started: "Started",
    duration: "Length",
    running: "Still open",
    activityCount: "Activities",
    notRecorded: "Nothing kept",
    /** ⚠️ ADR-030 — zero events and "kept nothing" are different facts. */
    notRecordedWhy:
      "This sitting was not recorded, so nothing was kept from it. That is different from a child who did nothing.",
    reassign: "Wrong child?",
    reassignTitle: "Move this sitting to another child",
    reassignPick: "Who was actually playing?",
    reassignDo: "Move the sitting",
    reassignDone: (n: number) =>
      `Moved. ${n} activities now belong to the other child.`,
    /**
     * 🔴 THE HALF IT CANNOT FIX, SAID PLAINLY. Coins, stickers and streaks are
     * device-authored with no per-session ledger, so the server cannot move what
     * a sitting was worth. A teacher who assumes otherwise believes the
     * leaderboard has been corrected when it has not.
     */
    reassignPartial:
      "Coins, stickers and streaks are not moved — they are not recorded per sitting, so they stay where they were earned.",

    /* ── recent activity ── */
    activityTitle: "Recent activity",
    when: "When",
    game: "Game",
    what: "What",
    skill: "Skill",

    /*
     * ── strengths and gaps ──────────────────────────────────────────────────
     *
     * 🔴 THE COPY IS CAREFUL BECAUSE THE OLD PANEL WAS NOT. "Insight" ranks a
     * child's skills by how many events they produced and calls the top two
     * *"strengths"* — so a child who spent the week failing Letter Lagoon is
     * described to their teacher as strong at literacy. These strings describe
     * answers, and every one of them carries its denominator.
     *
     * ⚠️ NOTHING HERE IS A GRADE OR A LABEL FOR A CHILD. "Needs practice" is
     * about a skill in a period, not about a person, and the two refusals below
     * — "not started" and "too early to say" — exist so the screen never has to
     * render a verdict the evidence does not support.
     */
    strengthsTitle: "Strong and stuck",
    strengthsWhy:
      "From the answers themselves, compared with the rest of this class. Not from how much they played.",
    strengthsPeers: (n: number) =>
      n === 0
        ? "No classmates to compare with yet, so these are read against the answers alone."
        : n === 1
          ? "Compared with 1 classmate."
          : `Compared with ${String(n)} classmates.`,
    standingStrong: "Strong",
    standingSteady: "Going well",
    standingNeedsPractice: "Needs practice",
    standingNotEnough: "Too early to say",
    standingNotStarted: "Not started",
    /** 🔴 The fraction, always. Never a bare percentage — OD-39. */
    strengthsAccuracy: (right: number, of: number) =>
      `${String(right)} right of ${String(of)} answered`,
    strengthsClass: (pct: number) => `class ${String(pct)}%`,
    strengthsNotEnoughHint: (n: number) =>
      `Only ${String(n)} answers so far — not enough to say anything yet.`,
    strengthsNotStartedHint:
      "They have not played this yet. Hand them the tablet.",
    strengthsLevel: (n: number) => `Level ${String(n)}`,
    /**
     * 🔴 THE LINE THAT MAKES A GOOD AVERAGE SUSPICIOUS. `play.prisma`: *"Ceiling
     * advancement LOOKS IDENTICAL TO SUCCESS in every accuracy average, so the
     * ceiling count is the only signal that a level's content is too hard."*
     * Nothing in this product has ever shown it to anybody.
     */
    strengthsCeiling: (n: number) =>
      n === 1
        ? "Moved up 1 level without mastering it — the content may be too hard."
        : `Moved up ${String(n)} levels without mastering them — the content may be too hard.`,
    strengthsEmpty:
      "Nothing to compare yet. This fills in once they have answered a few questions.",

    /*
     * ── the register ────────────────────────────────────────────────────────
     *
     * 🔴 "REMOVE" IS THE WORD A TEACHER WILL REACH FOR AND THE WRONG ONE FOR
     * WHAT HAPPENS, so the copy spells out the difference. Nothing is deleted:
     * the child leaves this register and their record and history stay exactly
     * where they are. Deleting what a child did is their parent's right, it is
     * irreversible, and it is on a different screen entirely.
     */
    /*
     * ⚠️ THE MOVE COPY ALREADY EXISTED — `moveTitle`, `moveWhy`, `moveTo`,
     * `moveRoll`, `move`, `moved` are up in the "move class" block, written when
     * the panel was designed and then never rendered because the route was
     * admin-only. Reused rather than rewritten: two sets of strings for one
     * control is how two screens end up describing the same action differently.
     * Only the class picker's placeholder is new.
     */
    movePick: "Choose a class",
    removeTitle: "Take off this register",
    removeWhy:
      "Removes them from this class only. Their record, their progress and everything they have played stay exactly as they are — an admin can put them in another class at any time.",
    removeNotDelete: "This does not delete anything about the child.",
    removeAction: "Take off the register",
    removeConfirm: "Yes, take them off",
    removeDone: "Taken off the register.",

    /* ── adding a child ── */
    addTitle: "Add a child to this class",
    addWhy: "For a child who joined after the roster was imported.",
    addName: "Name",
    addRoll: "Roll number",
    addAction: "Add to the class",
    addDone: "Added.",
    /*
     * ⚠️ THE ADMIN'S TWO EXTRA FIELDS. They are here rather than in the school
     * tier's own copy file because they belong to one shared form — both tiers
     * now draw them (ADR-050), and two copies of "Parent's phone" in two files
     * is how one screen ends up asking for a "guardian" and the other for a
     * "parent".
     */
    addParentName: "Parent’s name",
    addParentPhone: "Parent’s phone",
    addParentPhoneHint:
      "Creates their account if this number is new, or links the existing one. This is the number we sign them in with.",

    /*
     * ── correcting a child already on the register ──
     *
     * ⚠️ THE LABELS ARE THE ADD FORM'S, REUSED RATHER THAN RE-WRITTEN. The two
     * forms ask for the same two things, and the reason `addParentName` lives in
     * this file at all — *"two copies of 'Parent's phone' in two files is how one
     * screen ends up asking for a 'guardian' and the other for a 'parent'"* —
     * applies just as much to one screen adding a child and another correcting
     * them.
     */
    editTitle: "Edit this child",
    editWhy:
      "Correct a spelling, or write down the family’s number so reports can reach them.",
    editOpen: "Edit",
    editNameAction: "Save the name",
    editNameDone: "Name saved.",
    editParentAction: "Link this parent",
    editParentDone: "Parent linked.",
    /*
     * 🔴 IT SAYS THE OLD PARENT LOSES THE CHILD, BECAUSE THEY DO. The server
     * allows a replacement — families change numbers — and audits it as one. A
     * teacher who thinks she is adding a second contact and is in fact removing
     * the first is the one mistake this form can make that nobody would notice.
     */
    editParentReplaceWarn:
      "This child already has a parent linked. A new number replaces them, and the old account stops seeing this child.",

    /* ── shared ── */
    dash: "—",
    saving: "Saving…",
    failed: "That did not save.",
    refused: "You cannot change this child.",
    cancel: "Cancel",
  },
} as const;

/**
 * Skill names, in the teacher's language.
 *
 * 🔴 KEYED ON THE PRISMA ENUM, NOT ON A PARALLEL LOWERCASE SET. `ENGINEERING-GUIDE.md` §5:
 * *"never invent a parallel client-side set hand-mapped at the API boundary —
 * the predecessor did exactly that and the two drifted."*
 */
export const SKILL_LABEL: Record<string, Record<Lang, string>> = {
  NUMERACY: { en: "Numbers" },
  LITERACY: { en: "Letters and words" },
  GENERAL_AWARENESS: { en: "The world around us" },
  SOCIAL_AWARENESS: { en: "Getting on with others" },
  MATCHING: { en: "Matching and sorting" },
  COORDINATION: { en: "Coordination" },
  BALANCE: { en: "Balance" },
  BODY_AWARENESS: { en: "Body awareness" },
};

/**
 * ⚠️ BANDS ARE NUMERALS IN BOTH LANGUAGES, DELIBERATELY — the `BAND_LABEL`
 * precedent. A band is named "3–4", never "age 4" (docs/README.md Conventions),
 * and the numerals are its name rather than a word to translate.
 */
export const BAND_LABEL: Record<string, string> = {
  BAND_3_4: "3–4",
  BAND_4_5: "4–5",
  BAND_5_6: "5–6",
};
